/**
 * Central Typesense search configuration for products_v2.
 * Connection (host, port, key, protocol) stays in env; query shape lives here.
 */

export const TYPESENSE_COLLECTION =
  process.env.NEXT_PUBLIC_TYPESENSE_COLLECTION || 'products_v2';

/** Popular successful queries (autocomplete + did-you-mean). */
export const POPULAR_QUERIES_COLLECTION =
  process.env.NEXT_PUBLIC_TYPESENSE_POPULAR_QUERIES_COLLECTION ||
  'popular_queries_store';

/** Minimum trimmed characters before query + product suggestions run. */
export const MIN_QUERY_CHARS_FOR_SUGGESTIONS = 3;

export function getTypesensePublicBaseUrl(): string {
  const protocol = process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL ?? 'http';
  const host = process.env.NEXT_PUBLIC_TYPESENSE_HOST ?? 'localhost';
  const port = Number(process.env.NEXT_PUBLIC_TYPESENSE_PORT) || 8108;
  const defaultPort = protocol === 'https' ? 443 : 80;
  const portSuffix = port === defaultPort ? '' : `:${port}`;
  return `${protocol}://${host}${portSuffix}`;
}

export function getTypesenseSearchOnlyHeaders(): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    'X-TYPESENSE-API-KEY':
      process.env.NEXT_PUBLIC_TYPESENSE_SEARCH_ONLY_API_KEY ?? '',
  };
}

/**
 * Appends `x-typesense-api-key` to a Typesense URL (matches server / curl style).
 * Use together with {@link getTypesenseSearchOnlyHeaders} for maximum compatibility.
 */
export function appendTypesenseApiKeyQuery(url: string): string {
  const key = process.env.NEXT_PUBLIC_TYPESENSE_SEARCH_ONLY_API_KEY ?? '';
  if (!key) return url;
  const join = url.includes('?') ? '&' : '?';
  return `${url}${join}x-typesense-api-key=${encodeURIComponent(key)}`;
}

/** Connection block shared by InstantSearch adapters. */
export function getTypesenseServerConfig() {
  return {
    apiKey: process.env.NEXT_PUBLIC_TYPESENSE_SEARCH_ONLY_API_KEY ?? '',
    nodes: [
      {
        host: process.env.NEXT_PUBLIC_TYPESENSE_HOST ?? 'localhost',
        port: Number(process.env.NEXT_PUBLIC_TYPESENSE_PORT) || 8108,
        protocol: process.env.NEXT_PUBLIC_TYPESENSE_PROTOCOL ?? 'http',
      },
    ],
  };
}

/** True when the user query is non-empty and contains only digits (trimmed). */
export function isNumericOnlySearchQuery(
  query: string | undefined | null,
): boolean {
  if (query == null) return false;
  const t = String(query).trim();
  return t.length > 0 && /^\d+$/.test(t);
}

/** Facet fields exposed on the search page (comma-separated for consumers). */
export const PRODUCT_SEARCH_FACETS_CSV =
  'brand,manufacturer,active_ingredients,categories.name,dosage,packaging,is_controlled,requires_prescription,is_antibiotic';

/** Base filter for published products in products_v2. */
export const TYPESENSE_PUBLISHED_PRODUCTS_FILTER = 'status_id:=2';

/**
 * Positive integer ids for a Typesense `categories.id:=[...]` clause.
 * Drops blanks and non-numeric values and returns a stable order.
 */
export function normalizeTypesenseCategoryIds(
  categoryIds?: ReadonlyArray<string | number> | null,
): string[] {
  if (!categoryIds?.length) return [];
  const unique = new Set<string>();
  for (const raw of categoryIds) {
    const id = String(raw ?? '').trim();
    if (/^[1-9]\d*$/.test(id)) unique.add(id);
  }
  return [...unique].sort((a, b) => Number(a) - Number(b));
}

/**
 * InstantSearch `Configure.filters` fragment for a navigation shop group (Type).
 * Matches products linked to the type (`types_id` / `types.id` / `type_id`) and,
 * when category ids are passed, products placed in those categories.
 * Categories are linked to a type through `category_type`; their products often
 * have a different `type_id`.
 * Set `NEXT_PUBLIC_TYPESENSE_LEGACY_TYPE_FILTER_ONLY=true` to keep the legacy
 * `type_id` clause in the type match (still OR'd with the multi-type fields).
 */
export function typesenseNavigationTypeInstantSearchFilter(
  typeId: string | number | undefined | null,
  categoryIds?: ReadonlyArray<string | number> | null,
): string {
  if (typeId === undefined || typeId === null || typeId === '') {
    return 'true';
  }
  const id = String(typeId).trim();
  // Keep legacy mode as an additive fallback to avoid hiding products that only
  // have the new multi-type fields indexed.
  const typeClause =
    process.env.NEXT_PUBLIC_TYPESENSE_LEGACY_TYPE_FILTER_ONLY === 'true'
      ? `(type_id:${id} || types_id:=${id} || types.id:=${id})`
      : `(types_id:=${id} || types.id:=${id} || type_id:=${id})`;

  const ids = normalizeTypesenseCategoryIds(categoryIds);
  if (!ids.length) return typeClause;
  return `(${typeClause} || categories.id:=[${ids.join(',')}])`;
}

/**
 * Legacy CMS sort strings → current Typesense sort clauses.
 * Keep in sync with home dynamic-product InstantSearch clients.
 */
const TYPESENSE_SORT_MIGRATIONS: Record<string, string> = {
  'in_stock:desc,_text_match:desc,sale_price:asc':
    'in_stock:desc,sale_price:asc,_text_match:desc',
  'in_stock:desc,_text_match:desc,sale_price:desc':
    'in_stock:desc,sale_price:desc,_text_match:desc',
  'popularity_score:desc':
    'in_stock:desc,_text_match:desc,popularity_score:desc',
  'in_stock:desc,popularity_score:desc':
    'in_stock:desc,_text_match:desc,popularity_score:desc',
};

export function normalizeTypesenseSort(sortBy?: string): string | undefined {
  if (!sortBy) return undefined;
  return TYPESENSE_SORT_MIGRATIONS[sortBy] ?? sortBy;
}

/**
 * Default parameters for the main catalog / InstantSearch product index.
 */
export const MAIN_SEARCH_ADDITIONAL_PARAMETERS = {
  query_by:
    'name,active_ingredients,generic_name,brand,search_aliases,sku,normalized_description',
  query_by_weights: '20,18,14,8,7,3,1',
  num_typos: '2,2,1,1,1,0,0',
  min_len_1typo: 5,
  min_len_2typo: 10,
  prefix: 'true,false,false,false,false,false,false',
  infix: 'off,off,off,off,off,always,off',
  text_match_type: 'sum_score',
  prioritize_exact_match: true,
  prioritize_token_position: true,
  prioritize_num_matching_fields: true,
  split_join_tokens: 'always',
  drop_tokens_threshold: 1,
  sort_by: 'in_stock:desc,_text_match:desc,popularity_score:desc',
  filter_by: TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
  facet_by: PRODUCT_SEARCH_FACETS_CSV,
  highlight_fields: 'name,generic_name,active_ingredients,brand',
  highlight_affix_num_tokens: 4,
  per_page: 24,
} as const;

/**
 * Main search when the query is digits-only (SKU / barcode lookup).
 */
export const NUMERIC_ONLY_MAIN_SEARCH_PARAMETERS = {
  ...MAIN_SEARCH_ADDITIONAL_PARAMETERS,
  query_by: 'sku,barcode',
  query_by_weights: '10,10',
  infix: 'always,always',
  num_typos: '0,0',
  filter_by: TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
  prefix: 'true,true',
  highlight_fields: 'sku,barcode',
} as const;

/**
 * Autocomplete / search suggestions (lighter query; includes out-of-stock hits).
 * Add-to-cart is hidden in the suggestion UI when the product is unavailable.
 */
export const AUTOCOMPLETE_SEARCH_ADDITIONAL_PARAMETERS = {
  query_by: 'name,brand,active_ingredients,generic_name,search_aliases',
  query_by_weights: '10,6,8,8,5',
  prefix: 'true',
  num_typos: '1',
  enable_analytics: false,
  sort_by: 'in_stock:desc,popularity_score:desc',
  filter_by: TYPESENSE_PUBLISHED_PRODUCTS_FILTER,
  per_page: 6,
  highlight_fields: 'name',
  include_fields:
    'id,name,brand,image,sale_price,price,slug,sku,popularity_score,quantity,in_stock,status_id,type_id,product_prices,characteristics',
  facet_by: 'categories.name',
  max_facet_values: 5,
} as const;
